import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ISupportTicket, ISupportTicketComment, TicketPriority, TicketState, TicketCategory, TicketSeverity, ISystemContext } from '../models/ticket.interface';

@Injectable({
  providedIn: 'root',
})
export class SupportService {
  private apiUrl = `${environment.apiUrl}/support`;

  constructor(private http: HttpClient) { }


  /**
   * Create a new support ticket with full Phase 1 fields
   * @param abstract - Brief summary (required)
   * @param logId - Optional error log ID
   * @param description - Full description (required)
   * @param category - Ticket category
   * @param severity - User-assessed severity
   * @param reproducibilitySteps - Optional steps to reproduce
   * @param expectedBehavior - Optional expected behavior
   * @param actualBehavior - Optional actual behavior
   * @param systemContext - Optional captured system context
   * @returns Observable of the created ticket
   */
  createTicket(
    abstract: string,
    logId: string,
    description: string,
    category: TicketCategory = TicketCategory.OTHER,
    severity: TicketSeverity = TicketSeverity.MEDIUM,
    reproducibilitySteps?: string,
    expectedBehavior?: string,
    actualBehavior?: string,
    systemContext?: ISystemContext
  ): Observable<ISupportTicket> {
    const url = `${this.apiUrl}`;
    const ticketData = {
      abstract,
      logId,
      description,
      category,
      severity,
      reproducibilitySteps,
      expectedBehavior,
      actualBehavior,
      systemContext,
    };

    return this.http.post<ISupportTicket>(url, ticketData, {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
    });
  }

  /**
   * Search for similar/duplicate tickets
   * @param query Search query
   * @param limit Max results to return
   * @returns Observable of similar tickets
   */
  searchSimilarTickets(query: string, limit: number = 5): Observable<ISupportTicket[]> {
    const url = `${this.apiUrl}/search/similar`;
    const params = `?query=${encodeURIComponent(query)}&limit=${limit}`;
    return this.http.get<ISupportTicket[]>(url + params, this.getHttpOptions());
  }


  /**
   * Get all tickets for the authenticated user
   * @returns Observable of the user's tickets
   */
  getUserTickets(page?: number, limit?: number): Observable<ISupportTicket[]> {
    let url = `${this.apiUrl}`; // Base URL

    // Dynamically construct query parameters
    const params = [];
    if (page !== undefined) {
      params.push(`page=${page}`);
    }
    if (limit !== undefined) {
      params.push(`limit=${limit}`);
    }

    // Append query parameters to URL
    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }

    return this.http.get<ISupportTicket[]>(url, this.getHttpOptions());
  }

  /**
   * Get a specific ticket by its ID
   * @param ticketId - ID of the ticket
   * @returns Observable of the ticket details
   */
  getTicketById(ticketId: string): Observable<ISupportTicket> {
    const url = `${this.apiUrl}/${ticketId}`;
    return this.http.get<ISupportTicket>(url, this.getHttpOptions());
  }

  /**
   * Add a comment to a specific ticket
   * @param ticketId - ID of the ticket
   * @param comment - Comment details
   * @returns Observable of the updated ticket
   */
  addCommentToTicket(ticketId: string, comment: ISupportTicketComment): Observable<ISupportTicket> {
    const url = `${this.apiUrl}/${ticketId}/comments`;
    return this.http.post<ISupportTicket>(url, comment, this.getHttpOptions());
  }

  /**
   * Admin: Get all tickets
   * @returns Observable of all support tickets
   */
  getAllTickets(): Observable<ISupportTicket[]> {
    const url = `${this.apiUrl}/admin/all`;
    return this.http.get<ISupportTicket[]>(url, this.getHttpOptions());
  }

  /**
   * Admin: Add a comment to any ticket
   * @param ticketId - ID of the ticket
   * @param comment - Comment details
   * @returns Observable of the updated ticket
   */
  addAdminCommentToTicket(ticketId: string, comment: ISupportTicketComment): Observable<ISupportTicket> {
    const url = `${this.apiUrl}/admin/${ticketId}/comments`;
    return this.http.post<ISupportTicket>(url, comment, this.getHttpOptions());
  }


  /**
   * Update a comment by index
   * @param ticketId - ID of the ticket
   * @param index - Index of the comment in the array
   * @param text - Updated text of the comment
   * @returns Observable of the updated support ticket
   */
  updateComment(ticketId: string, index: number, text: string): Observable<ISupportTicket> {
    const url = `${this.apiUrl}/${ticketId}/comments/${index}`;
    return this.http.put<ISupportTicket>(url, { text }, { headers: { 'Content-Type': 'application/json' } });
  }


  /**
   * Update a state
   * @param ticketId - ID of the ticket
   * @param newState - New state
   * @returns Observable of the updated support ticket
   */
  updateTicketState(ticketId: string, newState: TicketState): Observable<ISupportTicket> {
    const url = `${this.apiUrl}/admin/${ticketId}/state`;
    return this.http.put<ISupportTicket>(url, { newState }, { headers: { 'Content-Type': 'application/json' } });
  }

  /**
   * Delete a comment by index
   * @param ticketId - ID of the ticket
   * @param index - Index of the comment in the array
   * @returns Observable of the updated support ticket
   */
  deleteComment(ticketId: string, index: number): Observable<ISupportTicket> {
    const url = `${this.apiUrl}/${ticketId}/comments/${index}`;
    return this.http.delete<ISupportTicket>(url);
  }


  /**
   * Helper to get HTTP options with headers
   * @returns Object with HTTP headers
   */
  private getHttpOptions() {
    return {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };
  }
}
